import React from 'react';
import { useTranslation } from '@/i18n';
import { Selector } from '@/components/ui/inputs/selector';
import { TimePicker } from '@/components/ui/inputs/TimePicker';
import { DatePicker } from '@/components/ui/filters/DatePicker';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { TextArea } from '@/components/ui/inputs/TextArea';
import {
  useRegisterIncidentForm,
  type IncidentRecord,
} from '@/hooks/attendance/myAttendance/useRegisterIncidentForm';
import type { RegisterIncidentFormValues } from '@/schemas/attendance';
import { SidePanelFormLayout } from '@/components/layout';


interface RegisterIncidentFormProps {
  onSubmit?: (data: RegisterIncidentFormValues) => void;
  readOnly?: boolean;
  record?: IncidentRecord;
}

export const RegisterIncidentForm: React.FC<RegisterIncidentFormProps> = ({
  onSubmit,
  readOnly,
  record,
}) => {
  const { t: translate } = useTranslation();
  const {
    checkInDate,
    checkInTime,
    maxDate,
    handleDateChange,
    handleTimeChange,
    locations,
    locationValue,
    setLocationValue,
    incidentTypes,
    incidentTypeValue,
    setIncidentTypeValue,
    eventTypes,
    eventValue,
    setEventValue,
    commentValue,
    setCommentValue,
    effectiveReadOnly,
    isSubmitting,
    isReady,
    handleSubmit,
    errors,
    isSubmitted,
  } = useRegisterIncidentForm({ onSubmit, readOnly, record });

  return (
    <SidePanelFormLayout
      onSubmit={handleSubmit}
      data-testid="registro-incidencia-form"
    >
      <SidePanelFormLayout.Fields>
        <DatePicker
          label={translate('attendance:incident.form.date')}
          value={checkInDate}
          onChange={handleDateChange}
          maxDate={maxDate}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors.checkInDate ? 'error' : undefined}
          supportingText={
            errors.checkInDate?.message
              ? translate(errors.checkInDate.message)
              : undefined
          }
          placeholder="dd/mm/aaaa"
          required
        />

        <TimePicker
          label={translate('attendance:incident.form.time')}
          value={checkInTime}
          onChange={handleTimeChange}
          disabled={effectiveReadOnly || isSubmitting}
          placeholder="09:00:00"
          color={errors.checkInTime ? 'error' : undefined}
          supportingText={
            errors.checkInTime?.message
              ? translate(errors.checkInTime.message)
              : undefined
          }
          required
        />

        <Selector
          label={translate('attendance:incident.form.location')}
          placeholder={translate('attendance:incident.form.locationPlaceholder')}
          options={locations}
          value={locationValue}
          onChange={(val: string | string[]) => {
            if (Array.isArray(val)) {
              const first = val[0] ?? '';
              setLocationValue(first);
            } else {
              setLocationValue(val);
            }
          }}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors?.locationId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors?.locationId && isSubmitted
              ? translate(errors.locationId.message as string)
              : undefined
          }
          required
        />

        <Selector
          label={translate('attendance:incident.form.event')}
          placeholder={translate('attendance:incident.form.eventPlaceholder')}
          options={eventTypes}
          value={eventValue}
          onChange={(val: string | string[]) => {
            if (Array.isArray(val)) {
              const first = val[0] ?? '';
              setEventValue(first);
            } else {
              setEventValue(val);
            }
          }}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors?.eventId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors?.eventId && isSubmitted
              ? translate(errors.eventId.message as string)
              : undefined
          }
          required
        />

        <Selector
          label={translate('attendance:incident.form.incidentType')}
          placeholder={translate('attendance:incident.form.incidentTypePlaceholder')}
          options={incidentTypes}
          value={incidentTypeValue}
          onChange={(val: string | string[]) => {
            if (Array.isArray(val)) {
              const first = val[0] ?? '';
              setIncidentTypeValue(first);
            } else {
              setIncidentTypeValue(val);
            }
          }}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors?.incidentTypeId && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors?.incidentTypeId && isSubmitted
              ? translate(errors.incidentTypeId.message as string)
              : undefined
          }
          required
        />

        <TextArea
          label={translate('attendance:incident.form.comment')}
          placeholder={translate('attendance:incident.form.commentPlaceholder')}
          value={commentValue}
          onChange={(value) => setCommentValue(value)}
          disabled={effectiveReadOnly || isSubmitting}
          maxLength={80}
          required
          rows={2}
          color={errors.comment && isSubmitted ? 'error' : undefined}
          supportingText={
            errors.comment && isSubmitted
              ? translate(errors.comment.message as string)
              : undefined
          }
          showCharacterCount
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PrimaryButton type="submit" fullWidth isLoading={isSubmitting} disabled={!isReady || isSubmitting}>
            {translate('attendance:incident.form.submit')}
          </PrimaryButton>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default RegisterIncidentForm;
